/*
 * Copyright (c) Nexign, JSC, 2020-2024.
 */
/**
 * Types for Banner component
 *
 * @author Yakov Gushchin
 * @author Maxim Fedorov
 * @author Anton Panasenko
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.33.0
 * @since 1.0.0
 */

import {
    IHideBannerPayload,
    IRegistrationClickAlternativeBannerPayload,
    IRegistrationShowAlternativeBannerPayload,
} from '@nexign/scc-ui-kit/lib/components/Banner/types';
import { Action } from 'redux';

import { IActionWithPayload } from '../app/createAction';
import { REGISTRATION_ALTERNATIVE_BANNER_FAILURE } from './bannerConstants';
import * as constants from './bannerConstants';

export interface ICampaignBannerItem {
    campaignId: number;
    title: string;
    description: string;
    imageURL: string;
    desktopImageURL?: string;
    // типы tabletImageURL и mobileImageURL пока не используются
    // сделаны на будущее
    tabletImageURL?: string;
    mobileImageURL?: string;
    buttonText?: string;
}

export interface IBannerItem {
    id: number;
    buttonText?: string;
    clickHandler?: string;
    imageURL: string;
    desktopImageURL?: string;
    // типы tabletImageURL и mobileImageURL пока не используются
    // сделаны на будущее
    tabletImageURL?: string;
    mobileImageURL?: string;
    showHandler?: string;
    url?: string;
    type?: EBannerType;
}

export interface IAlternativeBannerAttrsResponse {
    button_text?: string;
    desktop?: string;
    image_big?: string;
    mobile: string;
    subtitle: string;
    tablet: string;
    title: string;
    url: string;
}

export interface IAlternativeBannerItemResponse {
    attrs: IAlternativeBannerAttrsResponse;
    cid: number;
    click_handler: string;
    id: number;
    show_handler: string;
    rid: number | null;
    weight: number;
}

/*
 * -------------------------------------------
 *           LOAD BANNER
 * -------------------------------------------
 */

export type TLoadBannerAction = Action<typeof constants.LOAD_BANNER>;

export interface ILoadBannerSuccessfulPayload {
    banners: IBannerItem[];
    totalCount: number;
}

export type TLoadBannerSuccessfulAction = IActionWithPayload<
    typeof constants.LOAD_BANNER_SUCCESSFUL,
    ILoadBannerSuccessfulPayload
>;

export interface ILoadBannerErrorPayload {
    error: string;
}

export type TLoadBannerErrorAction = IActionWithPayload<typeof constants.LOAD_BANNER_FAILED, ILoadBannerErrorPayload>;

export enum EBannerType {
    ALTERNATE = 'alternate',
    CAMPAIGN = 'campaign',
}

/*
 * -------------------------------------------
 *       HIDE BANNER CAMPAIGN
 * -------------------------------------------
 */

export type THideBannerAction = IActionWithPayload<typeof constants.HIDE_BANNER, IHideBannerPayload>;

export type THideBannerSuccessfulAction = Action<typeof constants.HIDE_BANNER_SUCCESSFUL>;

export interface IHideBannerErrorPayload {
    error: string;
}

export type THideBannerErrorAction = IActionWithPayload<typeof constants.HIDE_BANNER_ERROR, IHideBannerErrorPayload>;

/*
 * -------------------------------------------
 *       HIDE ALTERNATIVE BANNER CAMPAIGN
 * -------------------------------------------
 */

export interface IHideAlternativeBannerPayload {
    id: number;
}

export type THideAlternativeBannerAction = IActionWithPayload<
    typeof constants.HIDE_ALTERNATIVE_BANNER,
    IHideAlternativeBannerPayload
>;

export type THideAlternativeBannerSuccessfulAction = Action<typeof constants.HIDE_ALTERNATIVE_BANNER_SUCCESSFUL>;

export type THideAlternativeBannerErrorAction = IActionWithPayload<
    typeof constants.HIDE_ALTERNATIVE_BANNER_ERROR,
    IHideBannerErrorPayload
>;

/*
 * -------------------------------------------
 *       REGISTRATION ALTERNATIVE BANNER
 * -------------------------------------------
 */

export interface IRegistrationAlternativeBannerFailurePayload {
    error: string;
}

export type TRegistrationClickAlternativeBannerAction = IActionWithPayload<
    typeof constants.REGISTRATION_CLICK_ALTERNATIVE_BANNER,
    IRegistrationClickAlternativeBannerPayload
>;
export type TRegistrationShowAlternativeBannerAction = IActionWithPayload<
    typeof constants.REGISTRATION_SHOW_ALTERNATIVE_BANNER,
    IRegistrationShowAlternativeBannerPayload
>;
export type TRegistrationAlternativeBannerFailure = IActionWithPayload<
    typeof REGISTRATION_ALTERNATIVE_BANNER_FAILURE,
    IRegistrationAlternativeBannerFailurePayload
>;
